import api from './api'

// Types
export interface ShipperAgent {
  id: string
  shipperId: string
  freightForwarderId: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  code?: string
  isActive: boolean
  inviteId?: string
  createdAt: string
  updatedAt: string
  // Joined fields
  agentName?: string
  agentTradingName?: string
  agentDocument?: string
  agentEmail?: string
  isPlatformAgent?: boolean
  spreadPercentage?: number | null
}

export interface ShipperAgentInvite {
  id: string
  shipperId: string
  freightForwarderId?: string
  inviteEmail?: string
  inviteCompanyName?: string
  inviteContactName?: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  expiresAt: string
  createdAt: string
  agentName?: string
  shipperName?: string
}

export interface AvailableFreightForwarder {
  id: string
  name: string
  tradingName?: string
  document: string
  email?: string
  isPlatformAgent: boolean
}

export interface BlockedAgent {
  id: string
  shipperId: string
  freightForwarderId: string
  blockedAt: string
  blockedBy: string
  reason?: string
  createdAt: string
  updatedAt: string
  agentName?: string
  agentTradingName?: string
  agentDocument?: string
  agentEmail?: string
}

export interface InviteAgentRequest {
  freightForwarderId?: string
  email?: string
  companyName?: string
  contactName?: string
  phone?: string
}

// API calls

/**
 * List all agents for the current shipper
 */
export async function getShipperAgents(): Promise<ShipperAgent[]> {
  const response = await api.get('/shipper/agents')
  return response.data.agents
}

/**
 * List freight forwarders available to invite
 */
export async function getAvailableAgents(): Promise<AvailableFreightForwarder[]> {
  const response = await api.get('/shipper/agents/available')
  return response.data.freightForwarders
}

/**
 * Get pending invites sent by the shipper
 */
export async function getShipperInvites(): Promise<ShipperAgentInvite[]> {
  const response = await api.get('/shipper/agents/invites')
  return response.data.invites
}

/**
 * Invite an agent (existing FF or by email)
 */
export async function inviteAgent(data: InviteAgentRequest): Promise<ShipperAgentInvite> {
  const response = await api.post('/shipper/agents/invite', data)
  return response.data.invite
}

/**
 * Cancel a pending invite
 */
export async function cancelInvite(inviteId: string): Promise<void> {
  await api.delete(`/shipper/agents/invite/${inviteId}`)
}

/**
 * Resend a pending invite
 */
export async function resendInvite(inviteId: string): Promise<ShipperAgentInvite> {
  const response = await api.post(`/shipper/agents/invite/${inviteId}/resend`)
  return response.data.invite
}

/**
 * Remove an agent relationship
 */
export async function removeAgent(freightForwarderId: string): Promise<void> {
  await api.delete(`/shipper/agents/${freightForwarderId}`)
}

/**
 * Get blocked agents for the current shipper
 */
export async function getBlockedAgents(): Promise<BlockedAgent[]> {
  const response = await api.get('/shipper/agents/blocked')
  return response.data.blockedAgents
}

/**
 * Block an agent
 */
export async function blockAgent(freightForwarderId: string, reason?: string): Promise<BlockedAgent> {
  const response = await api.post(`/shipper/agents/${freightForwarderId}/block`, { reason })
  return response.data.blockedAgent
}

/**
 * Unblock an agent
 */
export async function unblockAgent(freightForwarderId: string): Promise<void> {
  await api.delete(`/shipper/agents/${freightForwarderId}/block`)
}
